import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { QuickSearchInput } from '~/features/catalog/components/quicksearch/QuickSearchInput';
import { useArticleSearchQueryParams } from '~/features/catalog/components/filter/useArticleSearchQueryParam';
import { useBasePath } from '~/common/hooks/useBasePath';
import { QUERY_SEARCH_PARAM_SEARCHTERM } from '~/features/catalog/mappers/catalogSearchQueryMapper';
import type { Area } from '~/common/types/area';
import { TgArea } from '~/common/types/area';
import { LucideArrowLeft, LucideSearchX } from 'lucide-react';
import { cn } from '~/common/utils/styleUtil';
import { QuickSearchSuggestionItem } from '~/features/catalog/components/quicksearch/QuickSearchSuggestionItem';
import type { AghArticle } from '~/common/types/aghArticle';
import type { BghArticle } from '~/common/types/bghArticle';
import { QuickSearchBghArticleItem } from '~/features/catalog/components/quicksearch/QuickSearchBghArticleItem';
import { QuickSearchAghArticleItem } from '~/features/catalog/components/quicksearch/QuickSearchAghArticleItem';
import { useQuickSearch } from '~/features/catalog/routes/resources/QuickSearchRoute';
import { useTranslation } from 'react-i18next';
import { ClientOnly } from 'remix-utils/client-only';
import { useHasTopBanner } from '~/shell/hooks/useHasTopBanner';
import { isArticleNumber } from '~/features/catalog/utils/articleUtil.ts';

export interface QuickSearchProps {
  area: Area;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
  isAuthenticated: boolean;
}

export function QuickSearch({ area, isOpen, onOpenChange, className, isAuthenticated }: Readonly<QuickSearchProps>) {
  const { urlSearchRequest } = useArticleSearchQueryParams();
  const { path } = useBasePath();
  const [liveSearchTerm, setLiveSearchTerm] = useState(urlSearchRequest.searchTerm ?? '');
  const navigate = useNavigate();
  const { hasTopBanner } = useHasTopBanner();
  const { result, search, isSearching } = useQuickSearch();
  const { t } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const handleOnFocus = () => {
    onOpenChange(true);

    if (searchInputRef.current) {
      const input = searchInputRef.current;
      const length = input.value.length;
      setTimeout(() => {
        input.setSelectionRange(length, length);
      }, 0);
    }
  };

  const handleOnClear = () => {
    setLiveSearchTerm('');
    onOpenChange(true);
    searchInputRef.current?.focus();
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLiveSearchTerm(event.target.value);
    search(event.target.value);
  };

  const onSuggestionClick = (suggestion: string) => {
    onOpenChange(false);
    setLiveSearchTerm(suggestion);
    const searchParams = new URLSearchParams();
    searchParams.set(QUERY_SEARCH_PARAM_SEARCHTERM, suggestion);
    navigate({
      pathname: `${path}/catalog`,
      search: `?${searchParams.toString()}`
    });
  };

  const onArticleClick = (artNo: string) => {
    onOpenChange(false);
    navigate(`${path}/catalog/article/${artNo}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onOpenChange(false);
    }
  };

  const handleOnClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      searchInputRef.current?.blur();
    }, 0);
  };

  const handleSearchButtonClick = useCallback(() => {
    if (isOpen) {
      onOpenChange(false);
      const searchParams = new URLSearchParams();
      searchParams.set(QUERY_SEARCH_PARAM_SEARCHTERM, liveSearchTerm);
      navigate({
        pathname: `${path}/catalog`,
        search: `?${searchParams.toString()}`
      });
    } else {
      onOpenChange(true);
      searchInputRef.current?.focus();
    }
  }, [isOpen, onOpenChange, liveSearchTerm, navigate, path]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLiveSearchTerm(urlSearchRequest.searchTerm ?? '');
    search(urlSearchRequest.searchTerm ?? '');
  }, [search, urlSearchRequest.searchTerm]);

  useEffect(() => {
    onOpenChange(isOpen);
  }, [isOpen, onOpenChange]);

  const isValidSearchTerm = liveSearchTerm.length >= 3;
  const selectedArticleNumber = isArticleNumber(liveSearchTerm) ? liveSearchTerm : undefined;

  return (
    <div className={cn('flex gap-4', className)}>
      {isOpen && (
        <button type="button" onClick={() => onOpenChange(false)} data-testid={'quicksearchdialog-close-button'}>
          <LucideArrowLeft />
        </button>
      )}
      <QuickSearchInput
        ref={searchInputRef}
        searchTerm={liveSearchTerm}
        onFocus={handleOnFocus}
        onClear={handleOnClear}
        onChange={handleOnChange}
        onSubmit={() => handleOnClose()}
        onKeyDown={handleKeyDown}
        onSearchButtonClick={handleSearchButtonClick}
      />
      <ClientOnly>
        {() =>
          isOpen && (
            <div
              className={cn(
                'fixed flex h-[calc(100dvh-90px)] left-0 z-40 top-[90px] pt-2 bg-white w-screen',
                hasTopBanner && 'top-[128px] h-[calc(100dvh-128px)]'
              )}
            >
              {isValidSearchTerm ? (
                <div
                  className="overflow-y-auto h-full w-full pt-2"
                  onTouchStart={() => {
                    const input = searchInputRef.current;
                    if (input && document.activeElement === input) {
                      input.blur();
                    }
                  }}
                >
                  {!isSearching &&
                    result.suggestions &&
                    result.suggestions?.length < 1 &&
                    result.articles &&
                    result.articles?.length < 1 && (
                      <div className={'flex flex-col items-center gap-4 text-secondary mt-8'}>
                        <LucideSearchX size={48} className={'text-secondary'} />
                        {t('Keine Artikel für diese Suche!')}
                      </div>
                    )}
                  <div className={'flex flex-col desktop:flex-row gap-6 px-4 desktop:px-4'}>
                    <div className={'flex-1 desktop:ml-[290px]'}>
                      {result?.suggestions && result.suggestions.length > 0 && (
                        <>
                          <p className={'font-bold text-lg mb-4'}>{t('Suchvorschläge')}</p>
                          <ul>
                            {result.suggestions?.slice(0, 5).map(suggestion => (
                              <QuickSearchSuggestionItem
                                key={suggestion}
                                searchTerm={liveSearchTerm}
                                suggestion={suggestion}
                                onClick={onSuggestionClick}
                              />
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                    {result?.articles && result.articles.length > 0 && (
                      <div className={'flex-1'}>
                        <p className={'font-bold mb-4 text-lg'}>{t('Produktempfehlungen')}</p>
                        <ul>
                          {result.articles?.map((article: AghArticle | BghArticle) =>
                            area === TgArea ? (
                              <QuickSearchBghArticleItem
                                key={article.articleNumber}
                                article={article as BghArticle}
                                onClick={onArticleClick}
                                isAuthenticated={isAuthenticated}
                                selectedArticleNumber={selectedArticleNumber}
                              />
                            ) : (
                              <QuickSearchAghArticleItem
                                key={article.articleNumber}
                                article={article}
                                onClick={onArticleClick}
                                isAuthenticated={isAuthenticated}
                              />
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={'flex flex-col items-center gap-4 text-secondary mt-8 w-screen'}>
                  {t('Suchvorschläge ab 3 Zeichen!')}
                </div>
              )}
            </div>
          )
        }
      </ClientOnly>
    </div>
  );
}
